import { ProductQuoteModel } from 'apps/productQuote/app';
import { SpaceModel } from 'apps/space/app';
import { App, AppRegistry, ArrayApp, Model, ModelRegistry } from 'core';
import { AddressModel, OrderModel } from 'generated/models';
import { action, computed, observable } from 'mobx';
import { graphqlIdToDecimal } from 'utils/dgraphId';
import { addBusinessDays, parseISO, differenceInDays } from 'date-fns';
import { getCurrencyFormat } from 'apps/utils';

export class ShipmentQuoteModel extends Model {
  static registryName = 'ShipmentQuoteModel';

  static ModelProps = {
    productQuotes: ['ProductQuoteModel'],
    customerSpace: 'SpaceModel',
    shippingQuote: 'ShippingQuoteModel',
    miscItems: ['MiscItemModel'],
  };

  // should we have this set on the ShipmentQuoteModel?
  // (currently not part of the graphql schema)
  // should be called supplier
  @observable manufacturer: any = null;

  // Is not part of the graphql schema for type ShipmentQuote - complex relationship to ProductQuotes
  @observable currency = '';

  // Is not part of the graphql schema for 'type ShipmentQuote' (though it is in the input schema)
  @observable notes: '';

  @observable incoterm = 'EXW';

  @observable downPayment = 100;

  @observable takeRate?: number;

  @observable manufacturingTime = 0;

  @observable secondPaymentDue = 0;

  @observable bufferTime = 0;

  @observable totalCost = 0; // computed?

  @observable shippingQuote: ShippingQuoteModel = null;

  @observable address: AddressModel = null;

  @observable productQuotes: ProductQuoteModel[] = [];

  @observable miscItems: MiscItemModel[] = [];

  @observable orders: OrderModel[] = [];

  @observable customerSpace: SpaceModel = null;

  @observable createdAt = '';

  @observable validUntil = '';
}
ModelRegistry.add(ShipmentQuoteModel);

export class ShipmentQuoteApp extends App {
  id: string;

  validUntil: string;

  productQuotes: ProductQuotesArrayApp[];

  miscItems: any[];

  shippingQuote: ShippingQuoteApp;

  manufacturingTime: number;

  static registryName = 'ShipmentQuoteApp';

  static Model = ShipmentQuoteModel;

  static AppProps = {
    productQuotes: 'ProductQuotesArrayApp',
    customerSpace: 'SpaceApp',
    shippingQuote: 'ShippingQuoteApp',
    orders: 'OrdersArrayApp',
    miscItems: 'MiscItemsArrayApp',
  };

  @observable submitting = false;

  @observable isOrder = false;

  @computed get decimalId() {
    return graphqlIdToDecimal(this.id);
  }

  @action SetSubmitting = (boolVal: boolean) => {
    this.submitting = boolVal;
  };

  @computed get deliveryDate() {
    const startingDate = new Date();
    const manuPlusShippingTime =
      this.manufacturingTime +
      this.shippingQuote.deliveryTime +
      this.bufferTime;
    return addBusinessDays(startingDate, manuPlusShippingTime);
  }

  // @computed get currency() {
  //
  // }

  @computed get productQuoteCurrency() {
    return this.productQuotes.apps[0].currency;
  }

  @computed get productionCosts() {
    return this.productQuotes.reduce(
      (acc, productQuote) => acc + productQuote.cost,
      0,
    );
  }

  @computed get customerProductionCosts() {
    return this.productQuotes.reduce(
      (acc, productQuote) => acc + productQuote.finalCost,
      0,
    );
  }

  @computed get takeRateAmount() {
    return (this.customerProductionCosts * this.takeRate) / 100;
  }

  @computed get miscItemsTotalCost() {
    return this.miscItems.reduce(
      (acc: number, item: any) => acc + item.cost * (1 - item.discount / 100),
      0,
    );
  }

  @computed get daysRemaining() {
    const validUntilDate = parseISO(this.validUntil);
    const currentDate = new Date();

    return differenceInDays(validUntilDate, currentDate);
  }

  @computed get isExpired() {
    const validUntilDate = parseISO(this.validUntil);
    return validUntilDate < new Date();
  }
}
AppRegistry.add(ShipmentQuoteApp);

// NOTE(david): test approach
export type ShipmentQuoteAppT = ShipmentQuoteApp & ShipmentQuoteModel;

export class ShipmentQuotesArrayApp extends ArrayApp {
  static registryName = 'ShipmentQuotesArrayApp';

  static ElementApp = ShipmentQuoteApp;

  static AppProps = {};
}
AppRegistry.add(ShipmentQuotesArrayApp);

export class ShippingQuoteModel extends Model {
  static registryName = 'ShippingQuoteModel';

  @observable id: string;

  @observable isTaxInclusive = true;

  // For now we don't use the currency for anything on ShippingQuoteModel
  // @observable currency: string;

  @observable cost: number;

  @observable discount: number = 0;

  @observable deliveryTime: number;

  @observable provider: string = '';
}
ModelRegistry.add(ShippingQuoteModel);

export class ShippingQuoteApp extends App {
  cost: number;

  // deliveryTime: number;

  discount: number;

  // provider: string;

  static registryName = 'ShippingQuoteApp';

  static Model = ShippingQuoteModel;

  static AppProps = {};

  @computed get currency() {
    return this.parent.currency;
  }

  @computed get fmtAmount() {
    const { discount, cost, currency } = this;
    const hasDiscount = typeof discount === 'number' && discount >= 0;
    const hasCost = typeof cost === 'number' && cost >= 0;
    if (hasDiscount && hasCost) {
      const currencyFormat = getCurrencyFormat(currency);
      return currencyFormat.format(this.amount / 100.0);
    }
    return Number.NaN;
  }

  @computed get amount() {
    const { discount, cost } = this;
    return cost * (1 - discount / 100.0);
  }
}
AppRegistry.add(ShippingQuoteApp);

export class MiscItemModel extends Model {
  static registryName = 'MiscItemModel';

  @observable name: string;

  @observable cost: number;

  @observable currency: string;

  @observable discount: number = 0;
}
ModelRegistry.add(MiscItemModel);

export class MiscItemApp extends App {
  static registryName = 'MiscItemApp';

  static Model = MiscItemModel;

  @computed get computedCurrency() {
    if (this?.upstream?.currency) return this.upstream.currency;
    return this.parent.currency;
  }

  static AppProps = {};
}
AppRegistry.add(MiscItemApp);

export class MiscItemsArrayApp extends ArrayApp {
  static registryName = 'MiscItemsArrayApp';

  static ElementApp = MiscItemApp;

  static AppProps = {};
}
AppRegistry.add(MiscItemsArrayApp);
